/**
 * @fileoverview Type definitions for Teacher Signup flow
 * @description Interfaces for signup data, API requests and responses
 */

/**
 * Step 1 data - Basic user information
 */
export interface SignupStep1Data {
  email: string
  password: string
  firstName: string
  lastName: string
}

/**
 * Step 2 data - School selection
 */
export interface SignupStep2Data {
  grades: string[]
  schoolId: string
}

/**
 * Step 3 data - New school creation
 */
export interface SignupStep3Data {
  grades: string[]
  schoolName: string
  zipCode: string
}

/**
 * Complete signup data combining all steps
 */
export interface SignupData extends SignupStep1Data {
  grades: string[]
  schoolId?: string
  newSchoolName?: string
  zipCode?: string
  googleToken?: string
}

/**
 * School entity from database
 */
export interface School {
  id: string
  name: string
  city: string
  state: string
  zipcode: string
  street: string
}

/**
 * Grade option for select
 */
export interface Grade {
  value: string
  label: string
}

/**
 * Address information from zipcode lookup
 */
export interface AddressInfo {
  city: string
  state: string
  country: string
}

/**
 * User data returned from signup API
 * Backend returns snake_case, but api helper converts to camelCase
 */
export interface SignupUser {
  id: number
  email: string
  firstName: string
  lastName: string
  username: string
  usesSocialLogin: boolean
  provider: string | null
}

/**
 * Signup API success response from /api/arc_mosaico/auth/signup
 */
export interface SignupApiResponse {
  user: SignupUser
  message: string
}

/**
 * Signup API error response
 * Format: { errors: { field: ["message1", "message2"] } }
 */
export interface SignupApiError {
  errors: Record<string, string[]>
}

/**
 * Signup request payload for /api/arc_mosaico/auth/signup
 * Note: api helper converts camelCase to snake_case automatically
 */
export interface SignupRequest {
  email: string
  password: string
  firstName: string
  lastName: string
}

/**
 * Legacy signup response - kept for backward compatibility
 * @deprecated Use SignupApiResponse instead
 */
export interface SignupResponse {
  success: boolean
  message?: string
  userId?: string
}

/**
 * Firebase user data from Google OAuth popup
 */
export interface FirebaseUser {
  email: string
  uid: string
  displayName?: string
  photoURL?: string
  firstName?: string
  lastName?: string
}

/**
 * Firebase token manager data
 */
export interface FirebaseTokenManager {
  expirationTime: number
  accessToken: string
  refreshToken: string
}

/**
 * Google signup request payload for api/auth/google_auth/signup
 */
export interface GoogleSignupRequest {
  firebaseUser: {
    email: string
    uid: string
    displayName?: string
    firstName?: string
    lastName?: string
  }
  session?: {
    firebaseUser: {
      stsTokenManager: FirebaseTokenManager
    }
  }
}

/**
 * Onboarding request payload for complete_onboarding
 * ⚠️ Uses camelCase - api helper converts to snake_case automatically
 * ⚠️ IDs are UUIDs (strings), not numbers
 */
export interface OnboardingRequest {
  externalSchoolId: string
  gradeIds: string[]
}

/**
 * Onboarding response from complete_onboarding endpoint
 * Backend returns snake_case, api helper converts to camelCase
 */
/**
 * Tutor invite validation response from GET /api/arc_mosaico/tutor_invites/validate/{token}
 */
export interface TutorInviteValidation {
  valid: boolean
  error?: string
  email?: string
  tutor?: {
    id: string
    name: string
  }
  school?: {
    id: string
    name: string
  }
}

/**
 * Tutor invite signup request for POST /api/arc_mosaico/auth/tutor_invite_signup
 */
export interface TutorInviteSignupRequest {
  invitationToken: string
  email: string
  password: string
  passwordConfirmation: string
}

/**
 * Tutor invite signup response
 */
export interface TutorInviteSignupResponse {
  user: {
    id: string
    email: string
    firstName: string
    lastName: string
    currentProfile: string
  }
  message: string
}

export interface OnboardingResponse {
  success: boolean
  data: {
    user: {
      id: number
      email: string
      firstName: string
      lastName: string
      username: string
    }
    account: {
      id: number
      name: string
    }
    school: {
      id: number
      name: string
      externalSchoolId: number
      language: string
      timezone: string
    }
    schoolYear: {
      id: number
      name: string
      startAt: string
      endAt: string
    }
    group: {
      id: number
      name: string
      description: string
    }
  }
}
